import {useMutation, useQueryClient} from '@tanstack/react-query'

import {cancelGetAllMemexData} from '../../../api/memex/api-get-all-memex-data'
import {apiArchiveItems} from '../../../api/memex-items/api-archive-items'
import {apiUnarchiveItems} from '../../../api/memex-items/api-unarchive-items'
import useToasts from '../../../components/toasts/use-toasts'
import {useWrapMutationMutateAsync} from '../../../helpers/mutation-helpers'
import {ApiError} from '../../../platform/api-error'
import {HistoryResources, Resources} from '../../../strings'
import {useHistory} from '../../history/history'
import {useRemoveSuggestions} from '../../suggestions/use-remove-suggestions'
import {removeMemexItemsFromQueryClient} from '../query-client-api/memex-items/remove-items'
import {type OptimisticUpdateRollbackData, rollbackMemexItemData} from '../query-client-api/memex-items/update-items'

interface RevertArchiveMutationRequest {
  archivedItemIds: Array<number>
  rollbackData: OptimisticUpdateRollbackData | void
  rollbackRemoveParentFromChildren?: () => void
}

export const useArchiveItemsMutation = () => {
  const {removeSuggestions} = useRemoveSuggestions()
  const queryClient = useQueryClient()
  const {addToast} = useToasts()
  const history = useHistory()

  // We pushed these callbacks into the mutation definition (as opposed to at the call site) so that they will be called
  // even if the components that initiated the mutation are unmounted.
  // Without this approach, we would not be able to call the `onSuccess` and `onError` callbacks in conjunction with an optimistic update
  // as the component that triggered the removal might have been removed from the component hierarchy as part of the optimistic update.
  // See https://tkdodo.eu/blog/mastering-mutations-in-react-query#some-callbacks-might-not-fire for more details.
  const archiveMutation = useMutation({
    mutationFn: (request: {memexProjectItemIds: Array<number>}) => {
      return apiArchiveItems(request)
    },
    onMutate: request => {
      // Optimistically remove items
      const rollbackData = removeMemexItemsFromQueryClient(queryClient, request.memexProjectItemIds)
      cancelGetAllMemexData()
      return {rollbackData}
    },
    onSuccess: (_response, request, context) => {
      const rollbackRemoveParentFromChildren = undefined
      removeSuggestions(request.memexProjectItemIds)

      if (context && history) {
        history.registerAction({
          description: HistoryResources.archive(request.memexProjectItemIds.length),
          revert: () =>
            undoArchiveMutation.mutateAsync({
              archivedItemIds: request.memexProjectItemIds,
              rollbackData: context?.rollbackData,
              rollbackRemoveParentFromChildren,
            }),
        })
      }
    },
    onError: (error: unknown, _request, context) => {
      if (context?.rollbackData) {
        rollbackMemexItemData(queryClient, context.rollbackData)
      }
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({type: 'error', message: error instanceof ApiError ? error.message : Resources.genericErrorMessage})
    },
  })

  /**
   * Unarchive the items, ensuring they end up in the correct order, and also revert all changes to the project state.
   */
  const undoArchiveMutation = useMutation({
    mutationFn: ({archivedItemIds}: RevertArchiveMutationRequest) =>
      apiUnarchiveItems({memexProjectItemIds: archivedItemIds}),
    onMutate: ({rollbackData}) => {
      if (rollbackData) {
        rollbackMemexItemData(queryClient, rollbackData)
      }
      cancelGetAllMemexData()
    },
    onSuccess: () => {
      // We don't revert the `removeSuggestions` call. This is not an issue since that call was just to free up
      // memory by clearing unneeded values from the cache -- we will just refetch that data on demand
    },
    onError: (error: unknown, {archivedItemIds}) => {
      removeMemexItemsFromQueryClient(queryClient, archivedItemIds)

      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'error',
        message: `Failed to undo archive. ${error instanceof ApiError ? error.message : Resources.genericErrorMessage}`,
      })
    },
  })

  return useWrapMutationMutateAsync(archiveMutation)
}
